import React, { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import { collection, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import SegmentPopup from './SegmentPopup'
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { FaStar } from "react-icons/fa";

async function fetchTodayDeviceMemory(userId, selectedDate = new Date()) {

  // Convert the selected date to start and end of day timestamps
  const startOfDay = new Date(selectedDate);
  startOfDay.setHours(0, 0, 0, 0);
  const startTimestamp = Math.floor(startOfDay.getTime() / 1000);
  
  const endOfDay = new Date(selectedDate);
  endOfDay.setHours(23, 59, 59, 999);
  const endTimestamp = Math.floor(endOfDay.getTime() / 1000);

  const deviceQuery = query(
    collection(db, `EMA/${userId}/deviceMemory`),
    where("updatedAt", ">=", startTimestamp),
    where("updatedAt", "<=", endTimestamp)
  );
    
  const deviceSnapshot = await getDocs(deviceQuery);

  const deviceMemory = [];
  deviceSnapshot.forEach(doc => {
    const data = doc.data();
    deviceMemory.push({
      id: doc.id,
      start: data.updatedAt,
      end: null, // deviceMemory has no "end"
      value: data.lastSelectedMemoryIndex,
    });
  });
  
  return deviceMemory;
}

async function fetchSurveysByDate(userId, selectedDate = new Date()) {

  selectedDate.setHours(0, 0, 0, 0);
  const startTimestamp = Math.floor(selectedDate.getTime() / 1000); // seconds
  const endTimestamp = startTimestamp + 86400;
  const surveysRef = collection(db, "EMA", userId, "surveys");
  const surveys = [];

  const q = query(
    surveysRef,
    where("timestamp", ">=", startTimestamp),
    where("timestamp", "<", endTimestamp)
  );

  const snapshot = await getDocs(q);
  snapshot.forEach((doc) => {
    const data = doc.data();
    surveys.push({
      id: doc.id,
      start: data.timestamp,
      end: data.timestamp + 15 * 60,
      value: `${data.recordId}`,
      type: 'Survey'
    });
  });

  return surveys;
}

async function fetchMemoryRecordsForDay(userId, selectedDate = new Date()) {
  // Normalize selectedDate to start of day UTC
  selectedDate.setHours(0, 0, 0, 0);
  const startTimestamp = Math.floor(selectedDate.getTime() / 1000);
  const endTimestamp = startTimestamp + 86400; // +1 day in seconds

  const recordsRef = collection(db, "EMA", userId, "Records");

  // Query records where start is in selected day range
  const recordsQuery = query(
    recordsRef,
    where("start", ">=", startTimestamp),
    where("start", "<", endTimestamp),
  );

  const recordsSnapshot = await getDocs(recordsQuery);

  const memoryRecords = [];
  const surveyRecords = [];
  recordsSnapshot.forEach(doc => {
    const data = doc.data();
    if (data.type === "MemoryOnce" || data.type === "MemoryConsistent") {
      memoryRecords.push({
        id: doc.id,
        start: data.start,
        end: data.end ?? null,
        value: data.value,
        type: data.type
      });
    } else if (data.type === "Survey") {
      surveyRecords.push({
        id: doc.id,
        start: data.start,
        end: data.end ?? data.start + 15 * 60, // 15 minutes in seconds
        value: `surveyId: ${data.value} - recordId: ${data.createdAt}`,
        type: data.type
      });
    }
  });

  // Return both arrays as an object
  return { memoryRecords, surveyRecords };
}

const memoryColors = [
  '#4ade80', // 1
  '#2563eb', // 2 (changed from #60a5fa to a deeper blue)
  '#facc15', // 3
  '#f472b6', // 4
  '#a78bfa', // 5
];

const memoryLabels = [
  "1", 
  "2", 
  "3", 
  "4", 
  "5", 
];

const surveyColor = '#60a5fa'; // Tailwind blue-400

const TimelineBar = ({
  userId, 
  selectedDate,
  data,
  width = 900,
  height = 40,
  background = "#f3f4f6", // default to old color
}) => {
//console.log("data: ", data)
    const paddingLeft = 50;
    const paddingRight = 50;
    const barY = 10;
    const barHeight = 80;
    const barWidth = width - paddingLeft - paddingRight;
    selectedDate.setHours(0, 0, 0, 0);
    const startTimestamp = Math.floor(selectedDate.getTime() / 1000);
    const secondsInDay = 86400;
    const sorted = [...data].sort((a, b) => a.updatedAt - b.updatedAt);
    const [selectedSurvey, setSelectedSurvey] = useState(null);
    const [popupVisible, setPopupVisible] = useState(false);
    const segments = [];
    const getColor = (index) => {
        const valid = typeof index === 'number' && index >= 0 && index < memoryColors.length;
        return valid ? memoryColors[index] : surveyColor;
    };

    const formatTimestamp = (ts) => {
        const d = new Date(ts * 1000);
        return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
     };

    const handleSegmentClick = (seg) => {
      if (!userId) return;
      setSelectedSurvey(seg.value);
      setPopupVisible(true);
    };

    for (let i = 0; i < sorted.length; i++) {
        const curr = sorted[i];
        const isToday = new Date().toDateString() === selectedDate.toDateString();
        const segStart = ((curr.start - startTimestamp) / secondsInDay) * barWidth + paddingLeft;
        const segEnd = curr.end
        ? ((curr.end - startTimestamp) / secondsInDay) * barWidth + paddingLeft
        : i + 1 < sorted.length
          ? ((sorted[i + 1].start - startTimestamp) / secondsInDay) * barWidth + paddingLeft
          : (( (isToday ? Math.floor(Date.now() / 1000) : startTimestamp + secondsInDay) - startTimestamp) / secondsInDay) * barWidth + paddingLeft;
      
        const endTime = curr.end ? curr.end : i + 1 < sorted.length ? sorted[i + 1].start : isToday
        ? Math.floor(Date.now() / 1000)
        : startTimestamp + secondsInDay
        console.log("TimelineBar curr:", curr);

        const segWidth = Math.max(segEnd - segStart, 2);
        segments.push({
            x: segStart,
            width: segWidth,
            color: getColor(curr.value),
            value: curr.value,
            time: curr.start,
            endTime: endTime, // fallback if no end
            isSurvey: curr.type === 'Survey'
          });
      }
      
    console.log("TimelineBar segments:", segments);
    return (
      <>
       <svg
        width={width}
        height={height}
        style={{
          pointerEvents: background === 'transparent' ? 'none' : 'auto',
        }}
      >
        {/* Background bar */}
        <rect
          x={paddingLeft}
          y={barY}
          width={barWidth}
          height={barHeight}
          rx={8}
          fill={background}
        />

        {/* Segments */}
        {segments.map((seg, i) => {
          console.log("Rendering segment", i, seg);
          return (
            <g key={i}>
              <rect
                x={seg.x}
                y={barY}
                width={seg.width}
                height={barHeight}
                fill={seg.color}
                rx={4}
                style={{
                  cursor: userId ? "pointer" : "default",
                  pointerEvents: userId ? "auto" : "none"
                }}
                onClick={() => userId && handleSegmentClick(seg)}
              />
              <title>
                {formatTimestamp(seg.time)} to {formatTimestamp(seg.endTime)}
                {String(seg.value).length > 1 ? ` (${seg.value})` : ''}
              </title>

              {String(seg.value).length === 1 && (
                <text
                  x={seg.x + 4}
                  y={barY + 30}
                  fontSize={12}
                  fill={seg.color === '#a78bfa' ? '#fff' : '#000'}
                  fontWeight="bold"
                  pointerEvents="none"
                >
                  {/* {seg.value} */}
                </text>
              )}
              {seg.isSurvey && (
                <foreignObject
                  x={seg.x + seg.width / 2 - 8}
                  y={barY + barHeight / 2 - 8}
                  width={16}
                  height={16}
                  style={{ pointerEvents: "none" }}
                >
                  <div style={{ width: 16, height: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <FaStar color="#FFD700" size={16} />
                  </div>
                </foreignObject>
              )}
            </g>
          );
        })}
      </svg>

      <SegmentPopup
        visible={popupVisible}
        recordId={selectedSurvey}
        userId={userId}
        onClose={() => setPopupVisible(false)}
        formatTimestamp={formatTimestamp}
      />
      </>
    );
  };

const TimelineAxis = ({ width = 1100, height = 50, interval = 2 }) => {
    const hours = [];
    for (let i = 0; i <= 24; i += interval) {
      hours.push(i);
    }
  
    const paddingLeft = 50;
    const paddingRight = 50;
    const lineWidth = width;
    const usableWidth = lineWidth - paddingLeft - paddingRight;
  
    const axisY = 30; // Y position for axis line (adjust this to be below your segments bar)
  
    const formatHourLabel = (h) => {
      // e.g. convert 0-24 hour to AM/PM or just "0", "2", "4"...
      if (h === 0) return "12 AM";
      if (h === 12) return "12 PM";
      if (h > 12) return `${h - 12} PM`;
      return `${h} AM`;
    };
  
    return (
      <svg width={lineWidth} height={height}>
        {/* Axis line */}
        <line
          x1={paddingLeft}
          y1={axisY}
          x2={lineWidth - paddingRight}
          y2={axisY}
          stroke="#bbb"
          strokeWidth={2}
        />
  
        {/* Ticks and labels */}
        {hours.map((h) => {
          const x = paddingLeft + (h / 24) * usableWidth;
          let labelX = x;
          let textAnchor = "middle";
  
          if (h === 0) {
            labelX = x;
            textAnchor = "start";
          }
          if (h === 24) {
            labelX = lineWidth - paddingRight;
            textAnchor = "end";
          }
  
          return (
            <g key={h}>
              {/* Tick line */}
              <line
                x1={x}
                y1={axisY - 6}
                x2={x}
                y2={axisY + 6}
                stroke="#bbb"
                strokeWidth={2}
              />
              {/* Label */}
              <text
                x={labelX}
                y={axisY + 20}
                textAnchor={textAnchor}
                fontSize={12}
                fill="#888"
              >
                {formatHourLabel(h)}
              </text>
            </g>
          );
        })}
      </svg>
    );
  };

  
const StatisticsModal = ({ userId, open, onClose }) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [timelineDataMemory, setTimelineDataMemory] = useState([]);
  const [memoryRecords, setMemoryRecords] = useState([]);
  const [surveyRecords, setSurveyRecords] = useState([]);
  const [surveysDone, setSurveysDone] = useState([]);

  useEffect(() => {
    console.log('userId:', userId, 'selectedDate:', selectedDate);
    if (!userId || !selectedDate) return;
    fetchMemoryRecordsForDay(userId, selectedDate).then(result => {
      setMemoryRecords(result.memoryRecords);
      setSurveyRecords(result.surveyRecords);
    });
  }, [userId, selectedDate]);

  useEffect(() => {
    console.log("userId", userId)
    console.log("selectedDate", selectedDate, typeof selectedDate);
    fetchSurveysByDate(userId, selectedDate).then(result => {
      setSurveysDone(result);
    });
  }, [userId, selectedDate]);

  useEffect(() => {
    if (open && userId) {
      fetchTodayDeviceMemory(userId, selectedDate).then(setTimelineDataMemory);
    }
  }, [open, userId, selectedDate]);

  if (!open) return null;
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white p-12 rounded-2xl w-full max-w-7xl shadow-2xl relative" style={{ minHeight: 700 }}>
            <button
              onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full p-1 transition-colors"
          style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}
          aria-label="Close"
        >
          <FaTimes style={{ width: 20, height: 20 }} />
        </button>
        <span className="text-xs font-normal text-gray-500 italic">{userId}</span>

        <h2 className="text-4xl font-bold mb-10">Statistics</h2>
        
        {/* Date Picker */}
        <div className="mb-6 flex items-center justify-center gap-2">
          <button
            type="button"
            className="inline-flex items-center justify-center w-10 h-10 bg-blue-600 hover:bg-blue-700 text-white rounded text-xl"
            onClick={() => setSelectedDate(prev => {
              const d = new Date(prev);
              d.setDate(d.getDate() - 1);
              return d;
            })}
            aria-label="Previous day"
          >
            <FaChevronLeft />
          </button>
          <DatePicker
            selected={selectedDate}
            onChange={(date) => setSelectedDate(date)}
            dateFormat="yyyy-MM-dd"
            disabledKeyboardNavigation
            onKeyDown={(e) => e.preventDefault()}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-center w-full"
          />
          <button
            type="button"
            className="inline-flex items-center justify-center w-10 h-10 bg-blue-600 hover:bg-blue-700 text-white rounded text-xl"
            onClick={() => setSelectedDate(prev => {
              const d = new Date(prev);
              d.setDate(d.getDate() + 1);
              return d;
            })}
            aria-label="Next day"
          >
            <FaChevronRight />
            </button>
        </div>

        <div className="flex justify-center items-center" style={{ minHeight: 600 }}>
          <div className="border border-gray-200 rounded-lg p-6 bg-white" style={{ width: 1150, maxWidth: '95vw' }}>
          <div>
                <h3 style={{ marginTop: '16px', marginBottom: '4px', fontWeight: 'bold' }}>Trial Plan</h3>
                <div className="relative w-[1100px] h-[100px]">
                  {/* First timeline – background layer */}
                  <div className="absolute top-0 left-0">
                  <TimelineBar
                      selectedDate={selectedDate}
                      data={memoryRecords}
                      width={1100}
                      height={100}
                    />
                  </div>

                  {/* Second timeline – top layer, transparent background */}
                  <div className="absolute top-0 left-0 pointer-events-none">
                    <TimelineBar
                      selectedDate={selectedDate}
                      data={surveyRecords}
                      width={1100}
                      height={100}
                      background="transparent"
                    />
                  </div>
                </div>
                <h3 style={{ marginBottom: '4px', fontWeight: 'bold' }}>Field Execution</h3>
                <div className="relative w-[1100px] h-[100px]">
                  {/* First timeline – background layer */}
                  <div className="absolute top-0 left-0">
                    <TimelineBar
                      selectedDate={selectedDate}
                      data={timelineDataMemory}
                      width={1100}
                      height={100}
                    />
                  </div>

                  {/* Second timeline – top layer, transparent background */}
                  <div className="absolute top-0 left-0">
                    <TimelineBar
                      userId={userId}
                      selectedDate={selectedDate}
                      data={surveysDone}
                      width={1100}
                      height={100}
                      background="transparent"
                    />
                  </div>
                </div>
                <div className="flex justify-center mt-2">
                  <TimelineAxis width={1100} height={50} interval={2} />
                </div>
                <div className="mt-16 mb-4">
                  <div className="text-base font-semibold text-gray-800 mb-2 text-left">Memory legend</div>
                  <div className="flex items-center gap-4">
                    {memoryColors.map((color, idx) => (
                      <div key={color} className="flex items-center gap-1">
                        <span
                          className="inline-block w-5 h-5 rounded"
                          style={{ backgroundColor: color }}
                        ></span>
                        <span className="text-sm text-gray-700">{memoryLabels[idx]}</span>
                      </div>
                    ))}
                    <div key="survey-star" className="flex items-center gap-1">
                      <FaStar color="#FFD700" size={20} />
                      <span className="text-sm text-gray-700">Survey</span>
                    </div>
                  </div>
                </div>
            </div>
          </div>
        </div>
        {/* <div className="flex justify-center mt-2">
          <TimelineAxis width={1100} height={50} interval={2} />
        </div> */}

        </div>
      </div>
    );
  };
  
  export default StatisticsModal;